import { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { CircularProgress, Fade, alpha, makeStyles } from '@material-ui/core';
import clsx from 'clsx';

import { ReactComponent as LogoSvg } from '../../static/images/ngPulse.svg';
import { ReactComponent as LogoPgm } from '../../static/images/globalManager.svg';
import loginBackground from '../../static/images/login-background.png';
import { ColumnFlexLayout, RowFlexLayout } from '../layout';
import { ThemeOverride } from '../app/AppThemeProvider';
import { useGlobalDataService } from '../../services/ApiDataService';
import { useRequestState } from '../../utilities/Hooks';

const useStyles = makeStyles((theme) => ({
    root: {
        backgroundImage: `url(${loginBackground})`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        backgroundColor: '#3A6178', // this is hardcoded to match the background if it doesn't load

        overflowY: 'auto',

        color: alpha(theme.palette.common.white, 0.8),

        // Override text fields to look good in dark/light mode
        '& .MuiTextField-root': {
            color: alpha(theme.palette.common.white, 0.9),

            '& label': {
                color: alpha(theme.palette.common.white, 0.9),
            },

            '& .MuiInput-input': {
                color: theme.palette.common.white,
            },

            '& label.Mui-focused': {
                color: alpha(theme.palette.common.white, 0.8),
            },

            '& .MuiInput-underline:hover:before': {
                borderBottomColor: alpha(theme.palette.common.white, 0.5),
            },

            '& .MuiInput-underline:before': {
                borderBottomColor: alpha(theme.palette.common.white, 0.3),
            },

            '& .MuiInput-underline:after': {
                borderBottomColor: alpha(theme.palette.common.white, 0.8),
            },
        },
    },

    authPanel: {
        margin: theme.spacing(2),
        padding: theme.spacing(6, 8),
        boxShadow: theme.shadows[12],

        background: alpha(theme.palette.common.black, 0.25),

        borderRadius: 4,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: alpha(theme.palette.background.paper, 0.1),
    },

    logo: {
        alignSelf: 'center',
        marginBottom: theme.spacing(5),
    },
}));

export default function AuthPanel(props) {
    const { children, width, maxWidth, loading, className } = props;
    const classes = useStyles();
    const config = useLoginConfiguration();
    const panelStyle = useMemo(() => ({ width, maxWidth }), [maxWidth, width]);

    return (
        <ThemeOverride theme="dark">
            <ColumnFlexLayout className={clsx(classes.root, className)} vCenter grow>
                <RowFlexLayout hCenter>
                    <Fade in timeout={150}>
                        <div className={classes.authPanel} style={panelStyle}>
                            <ColumnFlexLayout>
                                <div className={classes.logo}>
                                    <div>
                                        <LogoSvg width={285} />
                                    </div>
                                    {config.isPgm() && (
                                        <div>
                                            <LogoPgm width={285} />
                                        </div>
                                    )}
                                </div>

                                {loading && (
                                    <RowFlexLayout hCenter vCenter>
                                        <CircularProgress variant="indeterminate" color="inherit" size={48} />
                                    </RowFlexLayout>
                                )}

                                {!loading && children}
                            </ColumnFlexLayout>
                        </div>
                    </Fade>
                </RowFlexLayout>
            </ColumnFlexLayout>
        </ThemeOverride>
    );
}

function useLoginConfiguration() {
    const dataService = useGlobalDataService();
    const { data, methods } = useRequestState();
    const config = useMemo(
        () => ({
            isPgm: () => data?.personality === 'PGM',
        }),
        [data]
    );

    useEffect(() => {
        getConfig();

        async function getConfig() {
            try {
                methods.onInit();
                methods.onLoad(await dataService.get('/ipm/public/ssoConfig/loginConfig'));
            } catch (e) {
                methods.onLoad({});
            }
        }
    }, [dataService, methods]);

    return config;
}

AuthPanel.propTypes = {
    children: PropTypes.node.isRequired,
    width: PropTypes.number,
    maxWidth: PropTypes.number,
    loading: PropTypes.bool,
};

AuthPanel.defaultProps = {
    width: 600,
    maxWidth: undefined,
    loading: false,
};
